import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ExplorerField, ExplorerRelation, ExplorerSchema } from '../api';
import { ancrages, ajusterVue, cadreDe, clePositions, emportees, placer, tonRelation, voisinage, zoomAutour, type Vue } from './explorer-graph-layout';
import { readPreference, savePreference } from './explorer-values';
export * from './explorer-graph-layout';

export interface GrapheProps {
  baseId: string; schema: ExplorerSchema; relations: ExplorerRelation[]; fields: ExplorerField[] | null;
  fieldsError?: boolean;
  selection: string | null; onSelect: (table: string) => void;
  onInspect: (relation?: ExplorerRelation) => void;
  active: boolean;
}
type Positions = Record<string, { x: number; y: number }>;

/** Toutes les tables sont visibles au départ ; le voisinage est un choix explicite. */
export function GrapheRelations({ baseId, schema, relations, fields, fieldsError, selection, onSelect, onInspect, active }: GrapheProps) {
  const boite = useRef<HTMLDivElement>(null);
  const markerId = useId().replace(/:/g, '');
  const [vue, setVue] = useState<Vue>({ k: 1, tx: 40, ty: 40 });
  const [positions, setPositions] = useState<Positions>(() => {
    const value = readPreference<unknown>(clePositions(baseId), {});
    if (!value || typeof value !== 'object') return {};
    return Object.fromEntries(Object.entries(value).filter(([, point]) => point && Number.isFinite(point.x) && Number.isFinite(point.y)));
  });
  const [survol, setSurvol] = useState<string | null>(null);
  const [focalise, setFocalise] = useState(false);
  const [depth, setDepth] = useState(1);
  const [q, setQ] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchIndex, setSearchIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const geste = useRef<{ table?: string; x: number; y: number; moved: boolean } | null>(null);
  const positionsRef = useRef(positions);
  positionsRef.current = positions;
  const allNodes = useMemo(() => placer(schema).map((node) => ({ ...node, ...(positions[node.table] ?? {}) })), [schema, positions]);
  const visible = useMemo(() => focalise && selection ? voisinage(selection, relations, depth) : null, [focalise, selection, relations, depth]);
  const nodes = useMemo(() => {
    if (!visible) return allNodes;
    const local = { ...schema, groups: schema.groups.map((group) => ({ ...group, tables: group.tables.filter((table) => visible.has(table.name)) })).filter((group) => group.tables.length) };
    return placer(local).map((node) => ({ ...node, ...(positions[node.table] ?? {}) }));
  }, [allNodes, visible, schema, positions]);
  const byName = useMemo(() => new Map(nodes.map((node) => [node.table, node])), [nodes]);
  const edges = useMemo(() => relations.filter((edge) => byName.has(edge.from) && byName.has(edge.to)), [relations, byName]);
  const cascade = useMemo(() => selection ? emportees(selection, relations) : new Set<string>(), [selection, relations]);
  const suggestions = allNodes.filter((node) => node.table.toLowerCase().includes(q.trim().toLowerCase()));
  const fieldCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const field of fields ?? []) counts.set(field.table, (counts.get(field.table) ?? 0) + 1);
    return counts;
  }, [fields]);
  const adjust = useCallback(() => {
    const element = boite.current;
    if (element?.clientWidth && element.clientHeight) setVue(ajusterVue(cadreDe(nodes), { w: element.clientWidth, h: element.clientHeight }));
  }, [nodes]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const autoFit = useRef(true);
  // Un panneau latéral ou un redimensionnement ne doit pas laisser le centre hors du plan.
  useEffect(() => {
    const element = boite.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let previous: { w: number; h: number } | null = null;
    const observer = new ResizeObserver(() => {
      const size = { w: element.clientWidth, h: element.clientHeight };
      if (!size.w || !size.h) return;
      if (autoFit.current) setVue(ajusterVue(cadreDe(nodesRef.current), size));
      else if (previous) { const old = previous; setVue((v) => ({ ...v, tx: v.tx + (size.w - old.w) / 2, ty: v.ty + (size.h - old.h) / 2 })); }
      previous = size;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const frameSignature = baseId + '|' + (focalise ? selection + ':' + depth : 'all');
  const lastFrame = useRef('');
  useEffect(() => {
    if (!active || !nodes.length || lastFrame.current === frameSignature) return;
    lastFrame.current = frameSignature;
    autoFit.current = true;
    adjust();
  }, [active, frameSignature, adjust, nodes.length]);
  // Une réorganisation demandée remet le cadrage à jour après le calcul des nouvelles positions.
  const [resetVersion, setResetVersion] = useState(0);
  const resetRef = useRef(0);
  useEffect(() => { if (resetVersion !== resetRef.current) { resetRef.current = resetVersion; adjust(); } }, [resetVersion, adjust]);
  const center = (name: string) => {
    const node = nodes.find((item) => item.table === name) ?? allNodes.find((item) => item.table === name);
    const element = boite.current;
    if (!node || !element) return;
    setVue((v) => ({ ...v, tx: element.clientWidth / 2 - (node.x + node.w / 2) * v.k, ty: element.clientHeight / 2 - (node.y + node.h / 2) * v.k }));
  };
  const findTable = (name: string) => { onSelect(name); center(name); setQ(''); setSearchOpen(false); onInspect(); };
  const point = (e: { clientX: number; clientY: number }) => {
    const rect = boite.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  };
  // Écoute non passive : la molette au-dessus du plan ne fait pas défiler aussi la page.
  useEffect(() => {
    const element = boite.current;
    if (!element) return;
    const wheel = (e: WheelEvent) => {
      e.preventDefault();
      autoFit.current = false;
      const rect = element.getBoundingClientRect();
      setVue((v) => zoomAutour(v, Math.exp(-Math.max(-100, Math.min(100, e.deltaY)) * .003), { x: e.clientX - rect.left, y: e.clientY - rect.top }));
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, []);
  const pointerDown = (e: React.PointerEvent, table?: string) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    geste.current = { table, ...point(e), moved: false };
  };
  const pointerMove = (e: React.PointerEvent) => {
    e.stopPropagation();
    const gesture = geste.current;
    if (!gesture) return;
    const p = point(e), dx = p.x - gesture.x, dy = p.y - gesture.y;
    if (!gesture.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
    gesture.moved = true; gesture.x = p.x; gesture.y = p.y; setDragging(true);
    autoFit.current = false;
    if (!gesture.table) { setVue((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy })); return; }
    const node = byName.get(gesture.table);
    if (!node) return;
    const previous = positionsRef.current[node.table] ?? node;
    const next = { ...positionsRef.current, [node.table]: { x: previous.x + dx / vue.k, y: previous.y + dy / vue.k } };
    positionsRef.current = next; setPositions(next);
  };
  const pointerUp = (e: React.PointerEvent) => {
    e.stopPropagation();
    const gesture = geste.current;
    geste.current = null; setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (gesture?.moved) { if (gesture.table) savePreference(clePositions(baseId), positionsRef.current); return; }
    if (gesture?.table) { onSelect(gesture.table); onInspect(); }
  };
  const groupLabels = nodes.filter((node, index) => !nodes.slice(0, index).some((previous) => previous.groupe === node.groupe && previous.x === node.x));
  return <section className="dcard explorer-graph" aria-label="Relations entre les tables">
    <div className="explorer-toolbar">
      <div className="explorer-graph-search">
        <input className="explorer-input" value={q} role="combobox" aria-label="Aller à une table" aria-autocomplete="list" aria-expanded={searchOpen} aria-controls={markerId + '-results'} aria-activedescendant={searchOpen && suggestions[searchIndex] ? markerId + '-result-' + searchIndex : undefined}
          placeholder="Aller à une table…" onFocus={() => setSearchOpen(true)} onBlur={(e) => { if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) setSearchOpen(false); }}
          onChange={(e) => { setQ(e.target.value); setSearchIndex(0); setSearchOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSearchOpen(false);
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setSearchOpen(true); setSearchIndex((index) => Math.max(0, Math.min(suggestions.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))); }
            if (e.key === 'Enter') { e.preventDefault(); if (suggestions[searchIndex]) findTable(suggestions[searchIndex]!.table); }
          }} />
        {searchOpen && <div className="explorer-suggestions" id={markerId + '-results'} role="listbox" aria-label="Tables de la base">
          {!suggestions.length && <p role="status">Aucune table ne correspond.</p>}
          {suggestions.map((node, index) => <button type="button" id={markerId + '-result-' + index} role="option" aria-selected={index === searchIndex} key={node.table}
            ref={(element) => { if (index === searchIndex) element?.scrollIntoView({ block: 'nearest' }); }}
            onMouseDown={(e) => e.preventDefault()} onClick={() => findTable(node.table)}>{node.table}<small>{node.groupe}</small></button>)}
        </div>}
      </div>
      <button className="explorer-button" aria-label="Dézoomer" onClick={() => { autoFit.current = false; setVue((v) => zoomAutour(v, 1 / 1.25, { x: (boite.current?.clientWidth ?? 0) / 2, y: (boite.current?.clientHeight ?? 0) / 2 })); }}>−</button>
      <span className="mono">{Math.round(vue.k * 100)} %</span>
      <button className="explorer-button" aria-label="Zoomer" onClick={() => { autoFit.current = false; setVue((v) => zoomAutour(v, 1.25, { x: (boite.current?.clientWidth ?? 0) / 2, y: (boite.current?.clientHeight ?? 0) / 2 })); }}>+</button>
      <button className="explorer-button" onClick={() => { autoFit.current = true; adjust(); }}>Ajuster</button>
      <div className="explorer-segment" role="group" aria-label="Tables affichées dans le graphe">
        <button aria-pressed={!focalise} onClick={() => { setFocalise(false); if (!focalise) { autoFit.current = true; adjust(); } }}>Toutes les tables</button>
        <button aria-pressed={focalise} disabled={!selection} onClick={() => setFocalise(true)}>Voisinage</button>
      </div>
      {focalise && <select className="explorer-input" aria-label="Profondeur du voisinage" value={depth} onChange={(e) => setDepth(Number(e.target.value))}><option value={1}>1 niveau</option><option value={2}>2 niveaux</option></select>}
      <span className="muted" role="status">{nodes.length} / {allNodes.length} tables affichées</span>
      <button className="explorer-button" onClick={() => { autoFit.current = true; setPositions({}); savePreference(clePositions(baseId), {}); setResetVersion((n) => n + 1); }}>Disposition d’origine</button>
    </div>
    {/* ⚠️ LA LÉGENDE PORTE DEUX ENCODAGES INDÉPENDANTS, et elle doit les séparer : la COULEUR dit ce
        que la suppression fait, le TRAIT dit si la base l'impose. Sans la seconde ligne, un pointillé
        se lirait comme une nuance de la première, et personne ne saurait que la plupart de nos liens
        ne sont garantis par rien. */}
    <div className="explorer-graph-legend">
      <span>→ référence une table</span><span className="explorer-cascade">Cascade</span>
      <span className="explorer-restrict">Suppression restreinte</span><span>Détachement / défaut</span>
      <span>— trait plein : contrainte imposée par la base</span>
      <span>·· pointillé : relation déclarée par le schéma, non imposée</span>
    </div>
    {!relations.length && <div className="explorer-info">Aucune clé étrangère déclarée : les tables sont affichées sans liens.</div>}
    <div ref={boite} className="explorer-canvas" style={{ cursor: dragging ? 'grabbing' : 'grab' }}
      onPointerDown={(e) => pointerDown(e)} onPointerMove={pointerMove} onPointerUp={pointerUp}
      onPointerCancel={() => { geste.current = null; setDragging(false); }}
      tabIndex={0} aria-label="Plan des tables. Flèches pour déplacer la vue, plus et moins pour zoomer, zéro pour ajuster."
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        const moves: Record<string, [number, number]> = { ArrowLeft: [50, 0], ArrowRight: [-50, 0], ArrowUp: [0, 50], ArrowDown: [0, -50] };
        if (moves[e.key]) { e.preventDefault(); autoFit.current = false; const [x, y] = moves[e.key]!; setVue((v) => ({ ...v, tx: v.tx + x, ty: v.ty + y })); }
        if (e.key === '0') { e.preventDefault(); autoFit.current = true; adjust(); }
        if (e.key === '+' || e.key === '-') { e.preventDefault(); autoFit.current = false; setVue((v) => zoomAutour(v, e.key === '+' ? 1.25 : 1 / 1.25, { x: (boite.current?.clientWidth ?? 0) / 2, y: (boite.current?.clientHeight ?? 0) / 2 })); }
      }}>
      <svg width="100%" height="100%" role="group" aria-label="Tables et relations">
        <defs><marker id={markerId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0 0 L8 4 L0 8 z" fill="context-stroke" /></marker></defs>
        <g transform={'translate(' + vue.tx + ',' + vue.ty + ') scale(' + vue.k + ')'}>
          {groupLabels.map((node) => <text key={node.table} x={node.x} y={node.y - 10} fontSize={12} fontWeight={600} fill="var(--ink500)" pointerEvents="none">{node.groupe}</text>)}
          {edges.map((edge) => {
            const a = byName.get(edge.from)!, b = byName.get(edge.to)!;
            const anchor = ancrages(a, b);
            const d = edge.from === edge.to ? 'M ' + (a.x + a.w) + ' ' + (a.y + 9) + ' c 26 -10 26 24 0 14'
              : 'M ' + anchor.x1 + ' ' + anchor.y1 + ' C ' + (anchor.x1 + anchor.c) + ' ' + anchor.y1 + ', ' + (anchor.x2 + (anchor.c2 ?? -anchor.c)) + ' ' + anchor.y2 + ', ' + anchor.x2 + ' ' + anchor.y2;
            const target = survol ?? selection;
            const highlighted = !target || edge.from === target || edge.to === target;
            return <g key={edge.name}>
              {/* ⚠️ LE POINTILLÉ NE DIT PLUS QU'UNE SEULE CHOSE : le lien n'est PAS imposé par la base.
                  Il redoublait `restrict`, que la COULEUR dit déjà — deux sens sur un même canal, et le
                  second arrivé aurait effacé le premier. Un canal, une signification : la couleur dit ce
                  que la suppression FAIT, le trait dit si quelqu'un l'impose. */}
              <path d={d} fill="none" stroke={tonRelation(String(edge.onDelete)).trait} strokeWidth={highlighted ? 2 : 1.2} strokeOpacity={highlighted ? 1 : .3}
                strokeDasharray={edge.enforced ? undefined : '5 3'} markerEnd={'url(#' + markerId + ')'} pointerEvents="none" />
              <path className="explorer-edge" d={d} fill="none" stroke="transparent" strokeWidth={12} role="button" tabIndex={0}
                aria-label={edge.from + '.' + edge.fromColumns.join(', ') + ' référence ' + edge.to + '.' + edge.toColumns.join(', ')}
                onPointerDown={(e) => e.stopPropagation()} onClick={() => onInspect(edge)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInspect(edge); } }} />
            </g>;
          })}
          {nodes.map((node) => <g key={node.table} className="explorer-node" role="button" tabIndex={0} aria-pressed={node.table === selection} aria-label={'Inspecter ' + node.table}
            transform={'translate(' + node.x + ',' + node.y + ')'}
            onPointerDown={(e) => pointerDown(e, node.table)} onPointerMove={pointerMove} onPointerUp={pointerUp}
            onMouseEnter={() => setSurvol(node.table)} onMouseLeave={() => setSurvol(null)}
            onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) center(node.table); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(node.table); onInspect(); } }}>
            <rect width={node.w} height={node.h} rx={7} fill={node.table === selection ? 'var(--in-bg)' : 'var(--paper)'} stroke={node.table === selection ? 'var(--teal-500)' : 'var(--ink200)'} strokeWidth={node.table === selection ? 2 : 1} />
            <text x={9} y={19} fontSize={12} fontWeight={600} fill="var(--ink900)">{node.table.length > 23 ? node.table.slice(0, 22) + '…' : node.table}</text>
            <text x={node.w - 8} y={19} textAnchor="end" fontSize={11} fill="var(--ink500)">{fields ? (fieldCount.get(node.table) ?? 0) + ' ch.' : fieldsError ? '?' : '…'}</text>
          </g>)}
        </g>
      </svg>
    </div>
    <div className="explorer-info">{nodes.length} / {allNodes.length} tables · {selection ? selection + ' : ' + cascade.size + ' autre(s) table(s) potentiellement concernée(s) par une suppression en cascade.' : 'Sélectionnez une table pour afficher ses détails.'} <span className="muted">Le schéma indique des dépendances, pas le nombre de lignes affectées.</span></div>
    <div className="explorer-graph-help">Molette : zoomer · Glisser : déplacer · Clic sur une table ou un lien : détails · Au clavier : Tab, Entrée et flèches.</div>
  </section>;
}
